import React, { useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import SearchField from '@amzn/meridian/search-field';
import Text from '@amzn/meridian/text';
import alertTokens from '@amzn/meridian-tokens/base/icon/alert';
import filterTokens from '@amzn/meridian-tokens/base/icon/filter';
import { AssignmentAssociate } from '../models';
import { getInitials } from '../../utils';
import AssociateNameRow from './AssociateNameRow';

export interface AssociateSidebarProps {
  unassignedAssociates: AssignmentAssociate[];
  searchQuery: string;
  onSearchChange: (value: string) => void;
  /**
   * Fires when the user clicks "Assign to..." with one or more associates
   * checked. Receives the list of selected associate ids in the order they
   * appear in `unassignedAssociates`.
   *
   * Optional: not used when `readOnly` is `true`, since the inline
   * "Assign to..." button and the per-row checkboxes are suppressed in
   * that mode and there is no caller for it.
   */
  onAssignClick?: (associateIds: string[]) => void;
  /**
   * When `true`, the sidebar renders in informational mode for the
   * Published Assignment Screen: per-row checkboxes are suppressed, the
   * inline "Assign to..." button is not rendered under any condition,
   * and no checkbox-selection state is maintained. The search field,
   * search-clear control, filter button, and unassigned-count badge
   * remain fully functional.
   *
   * Defaults to `false`, in which case behavior is bit-for-bit identical
   * to the editable Assignments page.
   */
  readOnly?: boolean;
}

/**
 * Left-column sidebar that lists the currently unassigned associates for
 * the Assignments page. Provides a case-insensitive name search, a filter
 * toggle, and an "Assign to..." action so the user can hand the selected
 * associates off to a workstation picker.
 *
 * Selection is checkbox-only and supports multi-select: any number of
 * associates can be checked, and the "Assign to..." button delivers all of
 * them to the parent in a single call. Clicking the row body itself does
 * not change selection — only the checkbox toggles it. After a successful
 * "Assign to..." the checked set is cleared so the next assignment starts
 * from a clean state.
 *
 * Presentational: `searchQuery` and the unassigned list are owned by the
 * parent page. The sidebar tracks which rows are checked
 * (`checkedAssociateIds`) and whether its filter panel is open
 * (`filterPanelOpen`).
 */
const AssociateSidebar: React.FC<AssociateSidebarProps> = ({
  unassignedAssociates,
  searchQuery,
  onSearchChange,
  onAssignClick,
  readOnly = false,
}) => {
  const [checkedAssociateIds, setCheckedAssociateIds] = useState<Set<string>>(
    new Set(),
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState<boolean>(false);

  const handleToggleFilterPanel = () => {
    setFilterPanelOpen((prev) => !prev);
  };

  const handleCheckboxChange = (associateId: string, nextChecked: boolean) => {
    setCheckedAssociateIds((prev) => {
      const next = new Set(prev);
      if (nextChecked) {
        next.add(associateId);
      } else {
        next.delete(associateId);
      }
      return next;
    });
  };

  // Derive the effective selection by intersecting the persistent checked
  // set with whatever is currently visible. Two effects:
  //   1. After a successful assignment the moved associates drop out of
  //      `unassignedAssociates`, so they silently disappear from the
  //      effective selection without us having to clear state explicitly.
  //   2. If the user opens the picker modal and then cancels, their
  //      checkboxes are preserved (we never imperatively cleared them).
  const effectiveSelectedIds = useMemo(() => {
    const visibleIds = new Set(
      unassignedAssociates.map((a) => a.associateId),
    );
    return new Set(
      Array.from(checkedAssociateIds).filter((id) => visibleIds.has(id)),
    );
  }, [checkedAssociateIds, unassignedAssociates]);

  const handleAssignClick = () => {
    if (effectiveSelectedIds.size === 0) return;
    if (!onAssignClick) return;
    // Preserve the order of the rendered list so the parent receives ids
    // in a stable, predictable order.
    const orderedIds = unassignedAssociates
      .map((a) => a.associateId)
      .filter((id) => effectiveSelectedIds.has(id));
    onAssignClick(orderedIds);
  };

  // Surface the count in the assign button label when more than one
  // associate is checked, matching common multi-select UX.
  const assignButtonLabel = useMemo(() => {
    const count = effectiveSelectedIds.size;
    if (count <= 1) return 'Assign to...';
    return `Assign ${count} to...`;
  }, [effectiveSelectedIds]);

  return (
    <Box className="assignments-associate-sidebar" spacingInset="300">
      {/* Toolbar: search field + filter toggle */}
      <Box className="assignments-associate-sidebar__toolbar">
        <Box className="assignments-associate-sidebar__search">
          <SearchField
            value={searchQuery}
            onChange={onSearchChange}
            onSubmit={onSearchChange}
            placeholder="Search associates"
            data-testid="associate-sidebar-search"
          />
        </Box>
        <Button
          type="icon"
          onClick={handleToggleFilterPanel}
          data-testid="associate-sidebar-filter-button"
        >
          <Icon tokens={filterTokens} />
        </Button>
      </Box>

      {/* Filter panel placeholder: visible when filterPanelOpen is true. */}
      {filterPanelOpen && (
        <Box
          className="assignments-associate-sidebar__filter-panel"
          spacingInset="200"
          data-testid="associate-sidebar-filter-panel"
        >
          <Text type="b200">Filters</Text>
        </Box>
      )}

      {/* "Unassigned" header with count badge. */}
      <Box className="assignments-associate-sidebar__header">
        <Text type="h100">Unassigned</Text>
        <Box
          className="assignments-associate-sidebar__count-badge"
          data-testid="associate-sidebar-count-badge"
        >
          <Text type="b200">{unassignedAssociates.length}</Text>
        </Box>
      </Box>

      {/* Associate list using the shared AssociateNameRow component. Only
          the checkbox toggles selection; the row body is non-interactive
          for selection purposes (no `onClick` is passed).

          The "Assign to..." button is rendered inline at the end of the
          list (rather than pinned to the bottom of the sidebar) so it
          stays close to the rows the user just checked, matching the
          mock. It's hidden until at least one associate is checked.

          When `readOnly` is `true` (Published Assignment Screen): every
          row renders without a checkbox and the inline assign button is
          suppressed. The search field, filter button, and unassigned-count
          badge above remain fully functional. */}
      <Box className="assignments-associate-sidebar__list">
        {unassignedAssociates.map((associate) => {
          const isChecked = !readOnly && effectiveSelectedIds.has(associate.associateId);
          return (
            <AssociateNameRow
              key={associate.associateId}
              associateName={associate.associateName}
              associateAlias={associate.associateAlias}
              showCheckbox={!readOnly}
              checkboxChecked={isChecked}
              onCheckboxChange={
                readOnly
                  ? undefined
                  : (nextChecked) =>
                      handleCheckboxChange(associate.associateId, nextChecked)
              }
              showCheckmark={false}
              hasWarning={associate.hasWarning}
              isSelected={isChecked}
              data-testid={`associate-sidebar-row-${associate.associateId}`}
            />
          );
        })}

        {!readOnly && effectiveSelectedIds.size > 0 && (
          <button
            type="button"
            className="assignments-associate-sidebar__assign-inline"
            onClick={handleAssignClick}
            data-testid="associate-sidebar-assign-button"
          >
            {assignButtonLabel}
          </button>
        )}
      </Box>
    </Box>
  );
};

export default AssociateSidebar;
